import { Action, Mutation, Module, VuexModule } from "vuex-module-decorators";
import { Actions, Mutations } from "@/store/enums/StoreEnums";
import ApiService from "@/core/services/ApiService";

/**
 * NIST AI Risk Management Framework (AI RMF) — módulo de evaluación.
 *
 * Sigue la misma jerarquía Función -> Categoría -> Subcategoría -> Pregunta
 * que ya usa el módulo NIST CSF (ver AuthModule: CSFQuestionList/CSFAssesmentList),
 * pero aislado en su propio módulo Vuex (mismo patrón que BodyModule/
 * BreadcrumbsModule/ConfigModule) en vez de crecer AuthModule.
 *
 * Contrato de API esperado del backend (a coordinar con el equipo de backend,
 * ver Guía de implementación NIST AI RMF, sección 1.1):
 *   GET  /AIRMF/functions
 *   GET  /AIRMF/categories
 *   GET  /AIRMF/subcategories
 *   POST /AIRMF/questions                       { skip, take, filters }
 *   POST /AIRMF/assessments                     { companyId, name, description }
 *   GET  /AIRMF/assessments?companyId=           (a diferencia de CSF, sí filtra por compañía)
 *   GET  /AIRMF/assessments/{id}/answers
 *   POST /AIRMF/assessments/{id}/answers          { answers: [{ questionId, result }] }
 */

export interface AiRmfFunction {
  id: number;
  name: string;
  code: string; // GV | MP | MS | MG
}

export interface AiRmfCategory {
  id: number;
  idFunction: number;
  name: string;
  code: string; // ej. GOVERN-1
}

export interface AiRmfSubCategory {
  id: number;
  idCategory: number;
  name: string;
  code: string; // ej. GOVERN-1.1
}

export interface AiRmfQuestion {
  id: number;
  description: string;
  idSubCategory: number;
}

export interface AiRmfAssessment {
  id: number;
  companyId: string;
  name: string;
  description?: string;
  status?: string; // Open | InProgress | Closed
  createdAt?: string;
}

export interface AiRmfAssessmentQuestion {
  id: number;
  idQuestion: number;
  idAssessment: number;
  result: number | null; // escala 1-5
}

interface AiRmfQuestionListPayload {
  skip: number;
  take: number;
  filters?: Record<string, unknown>;
}

interface CreateAiRmfAssessmentPayload {
  companyId: string;
  name: string;
  description?: string;
}

interface SaveAiRmfAnswersPayload {
  assessmentId: number;
  answers: Array<{ questionId: number; result: number | null }>;
}

interface AiRmfState {
  functions: Array<AiRmfFunction>;
  categories: Array<AiRmfCategory>;
  subcategories: Array<AiRmfSubCategory>;
  questionList: Array<AiRmfQuestion>;
  assessmentList: Array<AiRmfAssessment>;
  currentAssessment: AiRmfAssessment | null;
  assessmentQuestions: Array<AiRmfAssessmentQuestion>;
}

@Module
export default class AiRmfModule extends VuexModule implements AiRmfState {
  errors: Array<string> = [];
  functions: Array<AiRmfFunction> = [];
  categories: Array<AiRmfCategory> = [];
  subcategories: Array<AiRmfSubCategory> = [];
  questionList: Array<AiRmfQuestion> = [];
  assessmentList: Array<AiRmfAssessment> = [];
  currentAssessment: AiRmfAssessment | null = null;
  assessmentQuestions: Array<AiRmfAssessmentQuestion> = [];

  /**
   * @returns las 4 funciones núcleo (Govern/Map/Measure/Manage)
   */
  get currentAiRmfFunctions(): Array<AiRmfFunction> {
    return this.functions;
  }

  /**
   * @returns las 19 categorías del AI RMF
   */
  get currentAiRmfCategories(): Array<AiRmfCategory> {
    return this.categories;
  }

  /**
   * @returns las 72 subcategorías del AI RMF
   */
  get currentAiRmfSubcategories(): Array<AiRmfSubCategory> {
    return this.subcategories;
  }

  /**
   * @returns el banco de preguntas del assessment
   */
  get currentAiRmfQuestionList(): Array<AiRmfQuestion> {
    return this.questionList;
  }

  /**
   * @returns los assessments de la compañía actual
   */
  get currentAiRmfAssessmentList(): Array<AiRmfAssessment> {
    return this.assessmentList;
  }

  /**
   * @returns el assessment activo (recién creado o seleccionado)
   */
  get currentAiRmfAssessment(): AiRmfAssessment | null {
    return this.currentAssessment;
  }

  /**
   * @returns las respuestas (score 1-5) del assessment activo
   */
  get currentAiRmfAssessmentQuestions(): Array<AiRmfAssessmentQuestion> {
    return this.assessmentQuestions;
  }

  @Mutation
  [Mutations.GET_AIRMF_FUNCTIONS_MUTATION](payload: Array<AiRmfFunction>) {
    this.functions = payload;
  }

  @Mutation
  [Mutations.GET_AIRMF_CATEGORIES_MUTATION](payload: Array<AiRmfCategory>) {
    this.categories = payload;
  }

  @Mutation
  [Mutations.GET_AIRMF_SUBCATEGORIES_MUTATION](
    payload: Array<AiRmfSubCategory>,
  ) {
    this.subcategories = payload;
  }

  @Mutation
  [Mutations.GET_AIRMF_QUESTIONS_MUTATION](payload: Array<AiRmfQuestion>) {
    this.questionList = payload;
  }

  @Mutation
  [Mutations.CREATE_AIRMF_ASSESSMENT_MUTATION](payload: AiRmfAssessment) {
    this.currentAssessment = payload;
    this.assessmentList = [...this.assessmentList, payload];
  }

  @Mutation
  [Mutations.GET_AIRMF_ASSESSMENTS_MUTATION](payload: Array<AiRmfAssessment>) {
    this.assessmentList = payload;
  }

  @Mutation
  [Mutations.SET_CURRENT_AIRMF_ASSESSMENT_MUTATION](
    payload: AiRmfAssessment | null,
  ) {
    this.currentAssessment = payload;
  }

  @Mutation
  [Mutations.GET_AIRMF_ASSESSMENT_ANSWERS_MUTATION](
    payload: Array<AiRmfAssessmentQuestion>,
  ) {
    this.assessmentQuestions = payload;
  }

  @Mutation
  [Mutations.SAVE_AIRMF_ANSWERS_MUTATION](
    payload: Array<AiRmfAssessmentQuestion>,
  ) {
    this.assessmentQuestions = payload;
  }

  @Mutation
  [Mutations.SET_AIRMF_ERROR_MUTATION](payload: Array<string>) {
    this.errors = payload;
  }

  @Action
  [Actions.GET_AIRMF_FUNCTIONS]() {
    return ApiService.get("/AIRMF/functions")
      .then(({ data }) => {
        this.context.commit(Mutations.GET_AIRMF_FUNCTIONS_MUTATION, data.data);
      })
      .catch(({ response }) => {
        this.context.commit(
          Mutations.SET_AIRMF_ERROR_MUTATION,
          response?.data?.errors ?? [
            "No fue posible cargar las funciones del AI RMF.",
          ],
        );
      });
  }

  @Action
  [Actions.GET_AIRMF_CATEGORIES]() {
    return ApiService.get("/AIRMF/categories")
      .then(({ data }) => {
        this.context.commit(Mutations.GET_AIRMF_CATEGORIES_MUTATION, data.data);
      })
      .catch(({ response }) => {
        this.context.commit(
          Mutations.SET_AIRMF_ERROR_MUTATION,
          response?.data?.errors ?? [
            "No fue posible cargar las categorías del AI RMF.",
          ],
        );
      });
  }

  @Action
  [Actions.GET_AIRMF_SUBCATEGORIES]() {
    return ApiService.get("/AIRMF/subcategories")
      .then(({ data }) => {
        this.context.commit(
          Mutations.GET_AIRMF_SUBCATEGORIES_MUTATION,
          data.data,
        );
      })
      .catch(({ response }) => {
        this.context.commit(
          Mutations.SET_AIRMF_ERROR_MUTATION,
          response?.data?.errors ?? [
            "No fue posible cargar las subcategorías del AI RMF.",
          ],
        );
      });
  }

  @Action
  [Actions.GET_AIRMF_QUESTIONS](payload: AiRmfQuestionListPayload) {
    return ApiService.post("/AIRMF/questions", payload)
      .then(({ data }) => {
        this.context.commit(Mutations.GET_AIRMF_QUESTIONS_MUTATION, data.data);
      })
      .catch(({ response }) => {
        this.context.commit(
          Mutations.SET_AIRMF_ERROR_MUTATION,
          response?.data?.errors ?? [
            "No fue posible cargar el banco de preguntas.",
          ],
        );
      });
  }

  @Action
  [Actions.CREATE_AIRMF_ASSESSMENT](payload: CreateAiRmfAssessmentPayload) {
    return ApiService.post("/AIRMF/assessments", payload)
      .then(({ data }) => {
        this.context.commit(
          Mutations.CREATE_AIRMF_ASSESSMENT_MUTATION,
          data.data,
        );
        return data.data as AiRmfAssessment;
      })
      .catch(({ response }) => {
        this.context.commit(
          Mutations.SET_AIRMF_ERROR_MUTATION,
          response?.data?.errors ?? ["No fue posible crear el assessment."],
        );
        return undefined;
      });
  }

  @Action
  [Actions.GET_AIRMF_ASSESSMENTS](payload: { companyId: string }) {
    // A diferencia de GET_CSF_ASSESSMENT_LIST (que ignora companyId), este
    // action sí filtra por compañía desde el día uno.
    return ApiService.query("/AIRMF/assessments", {
      params: { companyId: payload.companyId },
    })
      .then(({ data }) => {
        this.context.commit(
          Mutations.GET_AIRMF_ASSESSMENTS_MUTATION,
          data.data,
        );
      })
      .catch(({ response }) => {
        this.context.commit(
          Mutations.SET_AIRMF_ERROR_MUTATION,
          response?.data?.errors ?? ["No fue posible cargar los assessments."],
        );
      });
  }

  @Action
  [Actions.GET_AIRMF_ASSESSMENT_ANSWERS](payload: { assessmentId: number }) {
    return ApiService.get(
      `/AIRMF/assessments/${payload.assessmentId}`,
      "answers",
    )
      .then(({ data }) => {
        this.context.commit(
          Mutations.GET_AIRMF_ASSESSMENT_ANSWERS_MUTATION,
          data.data,
        );
      })
      .catch(({ response }) => {
        this.context.commit(
          Mutations.SET_AIRMF_ERROR_MUTATION,
          response?.data?.errors ?? [
            "No fue posible cargar las respuestas del assessment.",
          ],
        );
      });
  }

  @Action
  [Actions.SAVE_AIRMF_ANSWERS](payload: SaveAiRmfAnswersPayload) {
    return ApiService.post(
      `/AIRMF/assessments/${payload.assessmentId}/answers`,
      { answers: payload.answers },
    )
      .then(({ data }) => {
        this.context.commit(Mutations.SAVE_AIRMF_ANSWERS_MUTATION, data.data);
        return true;
      })
      .catch(({ response }) => {
        this.context.commit(
          Mutations.SET_AIRMF_ERROR_MUTATION,
          response?.data?.errors ?? ["No fue posible guardar las respuestas."],
        );
        return false;
      });
  }
}
